// A20 W8 — the walkthrough library, and the door to the recorder.
//
// This page is the FIRST REAL CALLER of the studio fence. Until it existed,
// `assertStudioAuthor` was enforced by nothing that a human could reach: the
// endpoint was mounted, the fence was written and unit-tested, and no app in
// either repo ever POSTed to it. A boundary with no caller is a boundary nobody
// has watched work.
//
// ── TWO HALVES WITH TWO DIFFERENT AUDIENCES ─────────────────────────────────
// The LIBRARY is a plain admin read (GET /help-assets sits behind requireAdmin),
// so any platform admin gets a real, useful page here.
//
// RECORDING and PUBLISHING need more: platform staff AND super_admin AND either
// the platform desk or a designated demo org. mayAuthorWalkthroughs() checks the
// first two — the third is not visible to a browser, because /users/me does not
// expose organization_id and STUDIO_DEMO_ORG_IDS is server-side config. So the
// affordance can be drawn for someone the server will refuse, and that is fine
// and deliberate: the refusal is rendered verbatim, it is the server's own
// sentence, and nothing was written. What must never happen is the reverse —
// this page deciding a write is allowed. It decides nothing.
//
// ── PLAYBACK, AND WHAT `published` NOW MEANS (A20 phase 3, C4) ──────────────
// Until this slice the save path wrote bytes that NOTHING could read: four
// routes were mounted and none of them served a recording, so every stored
// walkthrough was write-only and `published` was a flag with no reader. Both are
// closed here. The bytes arrive from GET /help-assets/:id/recording — our own
// origin, streamed by the server, with no storage URL anywhere in this file or
// on the wire — and they are inflated by the SAME function the bug replay uses.
//
// `published` is now the gate on those bytes, which is why the Play control
// reads differently on a draft: a draft is inside the studio fence (it may be an
// unmasked capture of this console that its author has not yet decided is fit to
// show), a published walkthrough is readable by any platform admin. The server
// decides that on the verified session; this page only says so in advance so a
// reader is not surprised by a 403.
//
// ── WHAT THE ROWS SAY, AND WHY THE WORDING IS FUSSY ─────────────────────────
// `capture_received` (bytes arrived) and `capture_available` (bytes are
// retrievable) are different facts, and migration 34's CHECK constraint exists
// precisely so a row cannot claim the second without a path. A draft that
// received bytes but stored none CANNOT be published, and the server says so
// with a 409. This page therefore shows three distinct recording states rather
// than a checkmark, because "no recording" and "a recording that did not store"
// send an author to two different places.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Film, Globe, FileEdit, Play, X } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { ApiError } from '@/lib/supabase';
import { Kpi } from '@/components/shared/Kpi';
import { Pill } from '@/components/shared/badges';
import { Btn } from '@/components/shared/primitives';
import { Skel } from '@/components/shared/SkeletonLoader';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { useCurrentUser } from '@/context/PersonaProvider';
import { mayAuthorWalkthroughs } from '@/lib/persona';
import { openStudio } from '@/components/reporter/StudioPanel';
import { MaskBanner, MaskPill } from './chips';
import { formatBytes, formatDuration, surfaceLabel } from './api';
import { ReplayPlayer } from './ReplayPlayer';
import {
  fetchWalkthroughEvents,
  listWalkthroughs,
  setWalkthroughPublished,
  type HelpAssetRow,
} from './studio-api';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface-raised)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5, verticalAlign: 'top' };

/**
 * The three recording states, named.
 *
 * `stored` is the only one that can be published; the other two are why the
 * publish control is disabled and why the reason is written next to it rather
 * than left for a 409 to explain after the click.
 */
type RecordingState = 'stored' | 'not-stored' | 'none';

function recordingStateOf(row: HelpAssetRow): RecordingState {
  if (row.capture_available) return 'stored';
  if (row.capture_received) return 'not-stored';
  return 'none';
}

const RECORDING_LABEL: Record<RecordingState, string> = {
  stored: 'Stored',
  'not-stored': 'Not stored',
  none: 'No recording',
};

const RECORDING_REASON: Record<RecordingState, string> = {
  stored: '',
  'not-stored': 'The browser sent a recording but it did not reach storage, so there is nothing to replay. Record it again.',
  none: 'This walkthrough was saved without a recording.',
};

/**
 * Playback is its own little state machine, and it fails INDEPENDENTLY of the
 * library read — the same division TicketDetail draws for the bug replay. A
 * walkthrough whose bytes will not load must not blank the table that lists it.
 *
 * `refused` is the one state the bug replay has no equivalent for: it is the
 * draft fence answering, which is a fact about who is asking rather than about
 * whether bytes exist, and it must not read as "there is no recording".
 */
type ReplayState =
  | { kind: 'idle' }
  | { kind: 'loading'; row: HelpAssetRow }
  | { kind: 'ready'; row: HelpAssetRow; events: unknown[] }
  | { kind: 'absent'; row: HelpAssetRow }
  | { kind: 'refused'; row: HelpAssetRow; message: string }
  | { kind: 'error'; row: HelpAssetRow; message: string };

export function StudioPage() {
  const user = useCurrentUser();
  // Convenience only — see the header. The server re-checks on every write.
  const mayAuthor = mayAuthorWalkthroughs(user?.role, user?.isAdmin);

  const [rows, setRows] = useState<HelpAssetRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [replay, setReplay] = useState<ReplayState>({ kind: 'idle' });

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    // Drop any open replay: the card describes a row from the list we are about
    // to replace, and a player left open over refreshed data would be showing a
    // walkthrough whose state on screen is no longer the one it was opened from.
    setReplay({ kind: 'idle' });
    try {
      setRows(await listWalkthroughs());
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublished = useCallback(async (row: HelpAssetRow) => {
    setBusyId(row.id);
    setActionError(null);
    try {
      const updated = await setWalkthroughPublished(row.id, !row.published);
      // Replace from the RESPONSE, not from the optimistic guess. If the server
      // changed anything else about the row, the table now shows what the server
      // actually holds rather than what this page assumed it would.
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      // The server's own sentence, verbatim: a 403 from assertStudioAuthor and a
      // 409 for "no stored recording" are different problems with different
      // fixes, and paraphrasing them would lose that.
      setActionError(err instanceof Error ? err.message : 'The change did not go through.');
    } finally {
      setBusyId(null);
    }
  }, []);

  /**
   * Load one walkthrough's bytes.
   *
   * The three failure branches are three different facts and are kept apart on
   * purpose. 403 is the draft fence (a fact about the reader); 404/410 mean the
   * bytes are not there (a fact about the walkthrough — and worth noting that
   * the row can honestly say `capture_available` while retention or an out-of-
   * band deletion has since removed the object); anything else is a malfunction
   * and must say so rather than quietly reading as "no recording".
   */
  const play = useCallback(async (row: HelpAssetRow) => {
    setReplay({ kind: 'loading', row });
    try {
      setReplay({ kind: 'ready', row, events: await fetchWalkthroughEvents(row.id) });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // The server's own sentence, verbatim: it names which fence refused.
        setReplay({ kind: 'refused', row, message: err.message });
        return;
      }
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        setReplay({ kind: 'absent', row });
        return;
      }
      setReplay({
        kind: 'error',
        row,
        message: err instanceof Error ? err.message : 'Could not load the recording.',
      });
    }
  }, []);

  const closeReplay = useCallback(() => setReplay({ kind: 'idle' }), []);

  const kpis = useMemo(() => {
    const published = rows.filter((r) => r.published).length;
    const publishable = rows.filter((r) => !r.published && r.capture_available).length;
    const broken = rows.filter((r) => r.capture_received && !r.capture_available).length;
    const unmasked = rows.filter((r) => !r.masked).length;
    return { published, publishable, broken, unmasked };
  }, [rows]);

  const skeleton = (
    <div className="card" style={{ padding: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>{[0, 1, 2].map((i) => <TableRowSkeleton key={i} cols={[220, 120, 90, 90, 90, 110]} />)}</tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Walkthrough Studio</h1>
          <p className="page-subtitle">
            Record a narrated walkthrough with the same capture engine that files bug reports, and keep it as
            help content instead of a ticket. Recording captures <strong>this console</strong> — the engine records
            the page it runs in, so a walkthrough here shows the licence console, not the customer-facing app.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
          {mayAuthor && (
            <Btn kind="primary" icon="clapperboard" onClick={openStudio}>
              Record a walkthrough
            </Btn>
          )}
        </div>
      </div>

      {/* Said plainly rather than left as a missing button. An admin who is not
          a super-admin can read this library and can do nothing else with it. */}
      {!mayAuthor && (
        <div
          className="card"
          style={{ padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}
        >
          Recording and publishing walkthroughs is restricted to platform staff with the super-admin role,
          working from the platform workspace or a designated demo workspace. The licence server enforces that
          on every save. You can read the library below.
        </div>
      )}

      {actionError && (
        <div
          role="alert"
          className="card"
          style={{ padding: '12px 14px', marginBottom: 16, fontSize: 13, border: '1px solid var(--severity-critical)', color: 'var(--text-primary)' }}
        >
          {actionError}
        </div>
      )}

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={rows.length === 0}
        emptyIcon="clapperboard"
        emptyTitle="No walkthroughs yet"
        emptyBody={
          mayAuthor
            ? 'Record one with the button above. It saves as a draft; publishing is a separate, deliberate step.'
            : 'Nothing has been recorded yet.'
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Published" value={kpis.published} icon="globe" hint={`of ${rows.length} walkthroughs`} />
          <Kpi label="Ready to publish" value={kpis.publishable} icon="file-edit" hint="drafts with a stored recording" />
          <Kpi
            label="Recording missing"
            value={kpis.broken}
            icon="alert-triangle"
            accent={kpis.broken > 0 ? '#C2410C' : undefined}
            hint={kpis.broken > 0 ? 'bytes arrived but did not store' : 'none'}
          />
          <Kpi
            label="Unmasked"
            value={kpis.unmasked}
            icon="eye"
            accent={kpis.unmasked > 0 ? '#DC2626' : undefined}
            hint={kpis.unmasked > 0 ? 'recorded with visible text' : 'all masked'}
          />
        </div>

        {/* ── The replay ────────────────────────────────────────────────────
            Above the table, not inside a row: rrweb-player owns a fixed-size
            stage that a table cell cannot give it, and the reader is looking at
            ONE walkthrough at a time by construction (opening another replaces
            this one, which is also why only one set of bytes is ever held). */}
        {replay.kind !== 'idle' && (
          <div className="card" style={{ padding: 0, marginBottom: 18 }}>
            <div
              style={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                gap: 16, padding: '14px 18px', borderBottom: '1px solid var(--border)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {replay.row.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {formatDuration(replay.row.duration_ms)} · {formatBytes(replay.row.recording_bytes)} ·
                  {' '}served through this server, never from storage
                </div>
              </div>
              <button
                type="button"
                onClick={closeReplay}
                title="Close the replay"
                aria-label="Close the replay"
                style={{
                  width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)',
                  background: 'var(--surface)', color: 'var(--text-secondary)', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >
                <X size={14} />
              </button>
            </div>

            <div style={{ padding: '16px 18px' }}>
              {replay.kind === 'loading' ? (
                <Skel height={420} borderRadius={8} />
              ) : replay.kind === 'refused' ? (
                <div
                  role="status"
                  style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}
                >
                  {replay.message}
                </div>
              ) : replay.kind === 'absent' ? (
                <div
                  role="status"
                  style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#FEF3C7', border: '1px solid #FDE68A', color: '#78350F', fontSize: 13, lineHeight: 1.5 }}
                >
                  This walkthrough says a recording was stored, but the server has no bytes for it. The object has
                  been removed from storage — walkthroughs are platform content that retention does not purge, so
                  this is worth an operator&rsquo;s attention rather than a shrug.
                </div>
              ) : replay.kind === 'error' ? (
                <div style={{ display: 'grid', gap: 10, justifyItems: 'start' }}>
                  <div
                    role="alert"
                    style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#FEE2E2', border: '1px solid #FECACA', color: '#7F1D1D', fontSize: 13, lineHeight: 1.5 }}
                  >
                    Could not load the recording: {replay.message}
                  </div>
                  <button onClick={() => play(replay.row)} style={btnSec}><RefreshCw size={13} /> Try again</button>
                </div>
              ) : (
                /* `masked` is the AUTHOR'S claim and nothing has verified it —
                   help_assets carries no server verdict column, unlike a ticket.
                   MaskBanner reads exactly that (no verdict ⇒ "claimed"), which
                   is why it is passed the flag alone and not a fabricated one. */
                <ReplayPlayer events={replay.events} banner={<MaskBanner masked={replay.row.masked} compact />} />
              )}
            </div>
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Walkthrough</th>
                  <th style={th}>Where</th>
                  <th style={th}>Recording</th>
                  <th style={th}>Author</th>
                  <th style={th}>State</th>
                  <th style={th}>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rec = recordingStateOf(row);
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...td, maxWidth: 340 }}>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{row.name}</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {row.slug}
                        </div>
                        {row.description && (
                          <div style={{ color: 'var(--text-secondary)', fontSize: 11.5, marginTop: 4 }}>
                            {row.description}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ color: 'var(--text-primary)' }}>{surfaceLabel(row.surface_key)}</div>
                        {row.route && (
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {row.route}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {rec === 'stored' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-primary)' }}>
                              <Film size={11} /> {formatDuration(row.duration_ms)}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {formatBytes(row.recording_bytes)}
                            </span>
                            <MaskPill masked={row.masked} />
                            {/* The whole point of C4: the bytes were write-only
                                until this control existed. Enabled for everyone
                                who can see the row — the server holds the draft
                                fence, and a button that hides itself would only
                                move the refusal somewhere the reader cannot read
                                it. The title says in advance what a draft will
                                cost a non-author, so a 403 is never a surprise. */}
                            <Btn
                              kind="secondary"
                              sm
                              onClick={() => play(row)}
                              title={
                                row.published
                                  ? 'Play this published walkthrough'
                                  : 'Drafts play back for studio authors only; the server checks on the session.'
                              }
                            >
                              <Play size={11} />
                              {replay.kind !== 'idle' && replay.row.id === row.id ? 'Playing' : 'Play'}
                            </Btn>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <Pill
                              bg={rec === 'not-stored' ? '#FFEDD5' : 'var(--bg-muted)'}
                              fg={rec === 'not-stored' ? '#C2410C' : 'var(--text-muted)'}
                            >
                              {RECORDING_LABEL[rec]}
                            </Pill>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 220, display: 'block' }}>
                              {RECORDING_REASON[rec]}
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>
                        {/* "not recorded" rather than a guessed name: author_email
                            is stamped from the verified session or left null. */}
                        {row.author_email || <span style={{ color: 'var(--text-muted)' }}>not recorded</span>}
                        {row.author_role && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{row.author_role}</div>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                          {row.published ? (
                            <Pill bg="#DCFCE7" fg="#15803D" dot>
                              <Globe size={10} /> Published
                            </Pill>
                          ) : (
                            <Pill bg="var(--bg-muted)" fg="var(--text-secondary)">
                              <FileEdit size={10} /> Draft
                            </Pill>
                          )}
                          {mayAuthor && (
                            <Btn
                              kind="secondary"
                              sm
                              disabled={busyId === row.id || (!row.published && rec !== 'stored')}
                              title={
                                !row.published && rec !== 'stored'
                                  ? 'A walkthrough with no stored recording cannot be published.'
                                  : undefined
                              }
                              onClick={() => togglePublished(row)}
                            >
                              {busyId === row.id ? 'Working…' : row.published ? 'Un-publish' : 'Publish'}
                            </Btn>
                          )}
                        </div>
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(row.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>
    </div>
  );
}

export default StudioPage;
